import { apiSlice } from './apiSlice';
import {
  ConfigLotWithCosts,
  ExportType,
  LocationProviderCreditStatus,
  SchoolConfig,
} from '../../@types/configuration';
import { createEntityAdapter, createSelector, EntityState } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { getApplicationTimestamp } from '../../utils/applicationTimestamp';

const configLotAdapter = createEntityAdapter<ConfigLotWithCosts>();

const initialState = configLotAdapter.getInitialState();

export const configApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getConfigLotWithCost: builder.query<EntityState<ConfigLotWithCosts, string>, void>({
      query: () => `/config-lot/${getApplicationTimestamp()}`,
      transformResponse: (response: ConfigLotWithCosts[]) =>
        configLotAdapter.setAll(initialState, response),
      providesTags: ['ConfigLotWithCosts'],
      keepUnusedDataFor: 300,
    }),
    updateSchoolConfig: builder.mutation({
      query: ({ schoolConfig }) => ({
        url: `/config/school-config`,
        method: 'PUT',
        body: schoolConfig,
      }),
      invalidatesTags: ['SchoolConfig'],
    }),
    getSchoolConfig: builder.query<SchoolConfig, void>({
      query: () => '/config/school-config',
      providesTags: ['SchoolConfig'],
      keepUnusedDataFor: 300,
    }),
    getLocationProviderCredits: builder.query<LocationProviderCreditStatus, void>({
      query: () => '/config/location-provider-credit-tracking',
      providesTags: ['LocationProviderCreditStatus'],
    }),
    getAllowedExportTypes: builder.query<ExportType[], void>({
      query: () => '/config/export-types',
      providesTags: ['ExportTypes'],
      keepUnusedDataFor: 300,
    }),
  }),
});

export const {
  useGetSchoolConfigQuery,
  useGetConfigLotWithCostQuery,
  useGetAllowedExportTypesQuery,
  useUpdateSchoolConfigMutation,
  useGetLocationProviderCreditsQuery,
} = configApiSlice;

export const selectConfigLotResult = configApiSlice.endpoints.getConfigLotWithCost.select();

const selectConfigLotWithCosts = createSelector(
  selectConfigLotResult,
  (configLotResult) => configLotResult.data
);

export const { selectAll: selectAllConfigLotWithCosts, selectById: selectConfigLotWithCostsById } =
  configLotAdapter.getSelectors<RootState>(
    (state) => selectConfigLotWithCosts(state) ?? initialState
  ); // maybe change to apiState?!
